import { useGetHomeScreenDashboardV3, useGlobalStore } from "controller";
import { useAtomValue } from "jotai";
import type { TicketIntervalType } from "shared";
import { overviewPeriodAtom } from "../store";
import type {
  HomeOverviewData,
  OverviewCardTone,
  OverviewCount,
  OverviewPeriod,
} from "../types";
import { useDashboardScope } from "./useDashboardScope";

export interface OverviewCountCard {
  id: keyof HomeOverviewData;
  label: string;
  tone: OverviewCardTone;
  value: number;
  /** Maps to the `type` switch inside TicketTableDrawer. */
  filterType: string;
  /**
   * Set when the card's number ignores the resource filter, so the UI can say so
   * rather than looking like it failed to update.
   */
  scopeNote?: string;
}

const CARDS: Array<Omit<OverviewCountCard, "value">> = [
  {
    id: "unresolved_count",
    label: "Active tickets",
    tone: "default",
    filterType: "unresolved",
  },
  {
    id: "at_risk_count",
    label: "SLA at risk",
    tone: "warning",
    filterType: "atRisk",
  },
  {
    id: "overdue_count",
    label: "Overdue",
    tone: "critical",
    filterType: "overDue",
  },
  {
    id: "unassigned_count",
    label: "Unassigned",
    tone: "default",
    filterType: "unassigned",
    // "Unassigned" means nobody is assigned, so scoping it by team member is a
    // contradiction — the API keeps it MSP-wide on purpose.
    // scopeNote: "Always MSP-wide — not affected by the team filter",
  },
];

/** `otherfilters` every home screen drawer passes down to the tickets table. */
export const HOME_SCREEN_FILTER = [
  { column: "home_screen", operator: "=", value: "true" },
];

/**
 * The overview period and the tickets table name the same window differently —
 * the drilldown has to translate or the drawer lists every ticket while the card
 * counted one day's worth. `all` maps to nothing on purpose: `getAllTickets`
 * drops a falsy interval, which is exactly "no date limit".
 */
const PERIOD_INTERVAL: Record<OverviewPeriod, TicketIntervalType | undefined> =
  {
    today: "day",
    last_week: "week",
    last_month: "month",
    previous_month: "previous_month",
    all: undefined,
  };

/** Counts arrive as strings except `unassigned_count`, and `"0"` is truthy. */
const toCount = (count: OverviewCount | undefined) =>
  Number(count?.tickets ?? 0);

/**
 * Queue counts for the current dashboard scope and period. Shared by the
 * Overview cards, the chat empty state and the header chips so all three read
 * the same query cache.
 */
export const useOverviewCounts = (): {
  cards: OverviewCountCard[];
  isLoading: boolean;
  resourceIds: string[];
  isAllResources: boolean;
  period: OverviewPeriod;
  /** The current period as the tickets table expects it. */
  interval: TicketIntervalType | undefined;
} => {
  const timezone = useGlobalStore((state) => state.timezone);
  const { mode, resourceIds, overviewBody, isReady } = useDashboardScope();
  const period = useAtomValue(overviewPeriodAtom);

  const { data, isLoading } = useGetHomeScreenDashboardV3(
    { filter: period, timezone, ...overviewBody },
    isReady,
  );

  const overviewData = data as HomeOverviewData | undefined;

  return {
    cards: CARDS.map((card) => ({
      ...card,
      value: toCount(overviewData?.[card.id]),
    })),
    isLoading,
    resourceIds,
    isAllResources: mode === "all",
    period,
    interval: PERIOD_INTERVAL[period],
  };
};
